import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TablePagination from "components/Pagination/Table.paginaition";
import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { UserServices } from "Services/userServices";

interface IUser {
  _id: string;
  registrationNumber?: string;
  membershipNumber?: string;
  name?: string;
  last_name?: string;
  username?: string;
  email?: string;
  mobile?: string;
  gender?: string;
  dob?: string;
  pinCode?: string;
  city?: string;
  centerId?: string;
  center_name?: string;
  createdAt?: string;
}

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : "-";

export default function Users() {
  const [users, setUsers] = useState<IUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchUsers = async (pageToLoad: number) => {
    setLoading(true);
    try {
      const res = await UserServices.getUsers({
        page: pageToLoad,
        limit: perPage,
        search: search || undefined,
        gender: gender || undefined,
      });
      if (res?.status === 200) {
        setUsers(res.data.data.users);
        setTotal(res.data.data.total);
      }
    } catch (err) {
      toast.error("Unable to fetch users");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page, perPage, gender]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchUsers(1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await UserServices.exportUsers({
        search: search || undefined,
        gender: gender || undefined,
      });
      if (res?.status === 200) {
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
        const fileURL = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = `users-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
        URL.revokeObjectURL(fileURL);
        toast.success("User CSV downloaded");
      }
    } catch (err) {
      toast.error("Unable to download users CSV");
    }
    setDownloading(false);
  };

  return (
    <Container fluid>
      <div className="py-4">
        <Row className="align-items-center mb-3">
          <Col>
            <h5 className="mb-0">Users</h5>
            <p className="small text-muted mb-0">{total} registered users</p>
          </Col>
          <Col xs="auto">
            <Button
              variant="primary"
              size="sm"
              onClick={downloadCsv}
              disabled={downloading}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              {downloading ? "Preparing..." : "Download CSV"}
            </Button>
          </Col>
        </Row>

        <Row className="mb-3 g-2">
          <Col md={4}>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Search by name, email, mobile, registration no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Select
              size="sm"
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Genders</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </Form.Select>
          </Col>
        </Row>

        <Card>
          <Card.Body className="p-0">
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th className="fs-12">#</th>
                  <th className="fs-12">Registration No.</th>
                  <th className="fs-12">Membership No.</th>
                  <th className="fs-12">Name</th>
                  <th className="fs-12">Username</th>
                  <th className="fs-12">Email</th>
                  <th className="fs-12">Mobile</th>
                  <th className="fs-12">Gender</th>
                  <th className="fs-12">DOB</th>
                  <th className="fs-12">City</th>
                  <th className="fs-12">Pin Code</th>
                  <th className="fs-12">Center</th>
                  <th className="fs-12">Registered On</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={13} className="text-center py-3 fs-12">
                      Loading users...
                    </td>
                  </tr>
                ) : users.length > 0 ? (
                  users.map((user, index) => (
                    <tr key={user._id}>
                      <td className="fs-12">{(page - 1) * perPage + index + 1}</td>
                      <td className="fs-12">{user.registrationNumber || "-"}</td>
                      <td className="fs-12">{user.membershipNumber || "-"}</td>
                      <td className="fs-12">
                        {[user.name, user.last_name].filter(Boolean).join(" ") || "-"}
                      </td>
                      <td className="fs-12">{user.username || "-"}</td>
                      <td className="fs-12">{user.email || "-"}</td>
                      <td className="fs-12">{user.mobile || "-"}</td>
                      <td className="fs-12 text-capitalize">{user.gender || "-"}</td>
                      <td className="fs-12">{formatDate(user.dob)}</td>
                      <td className="fs-12">{user.city || "-"}</td>
                      <td className="fs-12">{user.pinCode || "-"}</td>
                      <td className="fs-12">{user.center_name || user.centerId || "-"}</td>
                      <td className="fs-12">{formatDate(user.createdAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={13} className="text-center py-3 fs-12">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {total > 0 && (
          <div className="mt-3">
            <TablePagination
              total={total}
              currentPage={page}
              perPage={perPage}
              handlePageChange={setPage}
              setPerPage={setPerPage}
            />
          </div>
        )}
      </div>
    </Container>
  );
}
